import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building, BookOpen, Plus, X, Landmark, Pencil, Trash2, Upload, ExternalLink, IndianRupee, CalendarDays, FileText, WalletCards } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';
import BulkUploadModal from '../../components/BulkUploadModal';

const getAssetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('/')) return path;
  const base = (config.S3_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
  return base ? `${base}/${String(path).replace(/^\/+/, '')}` : '';
};

const getProgrammeLabel = (programme) => (
  programme?.name
  || programme?.programmeName
  || programme?.programName
  || programme?.title
  || ''
).trim();

const DialogField = ({ label, help, className = '', children }) => (
  <label className={`block space-y-1.5 ${className}`}>
    <span className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-slate-500">
      {label}
      {help && (
        <button
          type="button"
          title={help}
          aria-label={`${label} help`}
          onClick={(event) => {
            event.preventDefault();
            toast(help);
          }}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 text-[10px] font-black normal-case text-slate-600 hover:bg-indigo-100 hover:text-indigo-700"
        >
          ?
        </button>
      )}
    </span>
    {children}
  </label>
);

export default function AdminCollegeDetails({ scope = 'admin' }) {
  const { user } = useAuth();
  const { collegeName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInstitutionScope = scope === 'institution';
  const apiBase = isInstitutionScope ? '/api/institution-admin' : '/api/admin';
  const routeBase = isInstitutionScope ? '/institution' : '/admin';
  const [institution, setInstitution] = useState(null);
  const [courses, setCourses] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [incomeSummary, setIncomeSummary] = useState({});
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProgramme, setShowAddProgramme] = useState(false);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingProgramme, setEditingProgramme] = useState(null);
  const [listMode, setListMode] = useState('courses');
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [addingBankAccount, setAddingBankAccount] = useState(false);
  const [bankForm, setBankForm] = useState({
    beneficiaryName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
  });
  const [erpPlatforms, setErpPlatforms] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);
  const [connectingErp, setConnectingErp] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState({});
  const [savingOrganization, setSavingOrganization] = useState('');
  const [fieldMappings, setFieldMappings] = useState({});
  const [savingFieldMappings, setSavingFieldMappings] = useState('');
  const [showBulkUploadCourses, setShowBulkUploadCourses] = useState(false);
  const [showBulkUploadProgrammes, setShowBulkUploadProgrammes] = useState(false);
  const [newProgramme, setNewProgramme] = useState({
    name: '',
    slug: '',
	    category: 'Engineering & Tech',
	    position: '',
	    description: '',
  });
  const [newCourse, setNewCourse] = useState({
    programmeId: '',
    name: '',
    category: 'Engineering & Tech',
	    fee: '',
	    seats: 60,
	    position: '',
	    minimumQualification: '',
    lateralEntryAccepted: false,
  });
  const canCreateCourses = hasPermission(user, PERMISSIONS.COURSES_CREATE);
  const canEditCourses = hasPermission(user, PERMISSIONS.COURSES_EDIT);
  const isSuperAdmin = user?.role === 'admin';
  const canEditLinkedAccounts = hasPermission(user, PERMISSIONS.LINKED_ACCOUNTS_EDIT);
  const canConnectErp = ['institution_admin', 'cluster_admin'].includes(user?.role);
  const institutionCategory = institution?.category || '';
  const institutionCategoryLabel = institutionCategory || 'Category not set';
  const visibleProgrammes = programmes.filter(programme => getProgrammeLabel(programme));
  const programmeLabelById = (programmeId) => getProgrammeLabel(
    programmes.find(programme => String(programme._id || programme.id) === String(programmeId))
  );
  const coursePositionOptions = (programmeId, excludedCourseId = '') => {
    if (!programmeId) return [];
    const count = courses.filter(course => (
      String(course.programmeId || course.programme?._id || course.programme?.id) === String(programmeId)
      && String(course._id || course.id) !== String(excludedCourseId)
    )).length;
    return Array.from({ length: count + 1 }, (_, index) => index + 1);
  };

  useEffect(() => {
    fetchCollegeDetails();
  }, [collegeName]);

  useEffect(() => {
    if (!loading && institution?._id && searchParams.get('selectOrganization') === '1') {
      setShowBankDetails(true);
      fetchErpPlatforms();
      toast.success('Choose the Zoho Books organization to connect');
      const next = new URLSearchParams(searchParams);
      next.delete('selectOrganization');
      setSearchParams(next, { replace: true });
    }
  }, [loading, institution?._id]);

  const fetchCollegeDetails = async () => {
    try {
      const decodedName = decodeURIComponent(collegeName);
      let currentInstitution = null;
      if (isInstitutionScope) {
        const meRes = await axios.get(`${apiBase}/me`);
        currentInstitution = meRes.data?.institution || null;
      } else {
        const institutionsRes = await axios.get(`${apiBase}/institutions`, {
          params: { q: decodedName, limit: 20 },
        });
        const items = institutionsRes.data?.items || (Array.isArray(institutionsRes.data) ? institutionsRes.data : []);
        currentInstitution = items.find(item =>
          item.collegeName === decodedName || item.displayName === decodedName
        );
      }
      setInstitution(currentInstitution || null);

      if (user?.role === 'admin') {
        setCourses([]);
        setProgrammes([]);
        if (currentInstitution?._id) {
          const incomeRes = await axios.get(`${apiBase}/settlements`, {
            params: { institutionId: currentInstitution._id, live: 'false' },
          });
          setIncomeSummary(incomeRes.data?.summary || {});
          setMonthlyRevenue(incomeRes.data?.monthlyRevenue || []);
        }
        return;
      }

      const [coursesRes, programmesRes] = await Promise.all([
        axios.get(!isInstitutionScope && currentInstitution?._id ? `${apiBase}/courses?institutionId=${currentInstitution._id}` : `${apiBase}/courses`),
        axios.get(!isInstitutionScope && currentInstitution?._id ? `${apiBase}/programmes?institutionId=${currentInstitution._id}` : `${apiBase}/programmes`),
      ]);

      const collegeCourses = isInstitutionScope ? coursesRes.data : coursesRes.data.filter(c =>
        c.collegeName === decodedName
        || c.institutionName === decodedName
        || c.institution?.collegeName === decodedName
        || c.institution?.displayName === decodedName
      );
      setCourses(collegeCourses);
      setProgrammes(isInstitutionScope ? (programmesRes.data || []) : (programmesRes.data || []).filter(p =>
        !currentInstitution?._id
        || String(p.institutionId || p.institution?._id || p.institution?.id || p.institution) === String(currentInstitution._id)
      ));
    } catch (err) {
      toast.error('Failed to load institution details');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgramme = async () => {
    if (!institution?._id || !newProgramme.name.trim()) {
      toast.error('Programme name is required');
      return;
    }
    try {
      await axios.post(`${apiBase}/programmes`, {
        ...(!isInstitutionScope ? { institutionId: institution._id } : {}),
        name: newProgramme.name,
        slug: newProgramme.slug,
        category: institutionCategory,
        description: newProgramme.description,
        position: newProgramme.position || null,
      });
      toast.success('Programme created');
      setShowAddProgramme(false);
	      setNewProgramme({ name: '', slug: '', category: institutionCategory, position: '', description: '' });
      fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create programme');
    }
  };

  const handleCreateCourse = async () => {
    if (!institution?._id || !newCourse.programmeId || !newCourse.name.trim() || !newCourse.fee) {
      toast.error('Programme, course name and fee are required');
      return;
    }
    try {
      await axios.post(`${apiBase}/courses`, {
        ...(!isInstitutionScope ? { institutionId: institution._id } : {}),
        programmeId: newCourse.programmeId,
        name: newCourse.name,
        category: institutionCategory,
	        fee: newCourse.fee,
	        seats: newCourse.seats,
	        position: newCourse.position || null,
        minimumQualification: newCourse.minimumQualification,
        lateralEntryAccepted: Boolean(newCourse.lateralEntryAccepted),
      });
      toast.success('Course created');
      setShowAddCourse(false);
	      setNewCourse({ programmeId: '', name: '', category: institutionCategory, fee: '', seats: 60, position: '', minimumQualification: '', lateralEntryAccepted: false });
      fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create course');
    }
  };

  const openEditCourse = (course) => {
    setEditingCourse({
      id: course._id || course.id,
      programmeId: course.programmeId || course.programme?._id || course.programme?.id || '',
      name: course.name || '',
      category: course.category || 'Engineering & Tech',
      fee: course.fee || '',
      seats: course.seats || course.totalSeats || 60,
	      seatsAvailable: course.seatsAvailable ?? '',
	      position: course.position ?? '',
      minimumQualification: course.minimumQualification || '',
      lateralEntryAccepted: Boolean(course.lateralEntryAccepted),
      active: course.active !== false,
    });
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse?.id || !editingCourse.programmeId || !editingCourse.name.trim() || !editingCourse.fee) {
      toast.error('Programme, course name and fee are required');
      return;
    }
    try {
      await axios.patch(`${apiBase}/courses/${editingCourse.id}`, {
        programmeId: editingCourse.programmeId,
        name: editingCourse.name,
        category: editingCourse.category,
        fee: editingCourse.fee,
        seats: editingCourse.seats,
	        seatsAvailable: editingCourse.seatsAvailable === '' ? undefined : editingCourse.seatsAvailable,
	        position: editingCourse.position === '' ? null : editingCourse.position,
        minimumQualification: editingCourse.minimumQualification,
        lateralEntryAccepted: Boolean(editingCourse.lateralEntryAccepted),
        active: editingCourse.active,
      });
      toast.success('Course updated');
      setEditingCourse(null);
      fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update course');
    }
  };

  const handleDeleteLinkedAccount = async () => {
    if (!institution?._id) return;
    const confirmed = window.confirm('Remove this institution linked account and disable split payments?');
    if (!confirmed) return;
    try {
      await axios.delete(`/api/admin/institutions/${institution._id}/linked-account`);
      toast.success('Linked account removed');
      setShowBankDetails(false);
      fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove linked account');
    }
  };

  const openEditProgramme = (programme) => {
    setEditingProgramme({
      id: programme._id || programme.id,
      name: programme.name || '',
      slug: programme.slug || '',
      description: programme.description || '',
      position: programme.position ?? '',
      active: programme.active !== false,
    });
  };

  const handleUpdateProgramme = async () => {
    if (!editingProgramme?.id || !editingProgramme.name.trim()) {
      toast.error('Programme name is required');
      return;
    }
    try {
      await axios.patch(`${apiBase}/programmes/${editingProgramme.id}`, {
        ...(!isInstitutionScope ? { institutionId: institution._id } : {}),
        name: editingProgramme.name,
        slug: editingProgramme.slug,
        description: editingProgramme.description,
        position: editingProgramme.position === '' ? null : editingProgramme.position,
        active: editingProgramme.active,
      });
      toast.success('Programme updated');
      setEditingProgramme(null);
      fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update programme');
    }
  };

  const handleAddBankAccount = async () => {
    if (!institution?._id) return;
    if (!bankForm.beneficiaryName.trim() || !bankForm.accountNumber.trim() || !bankForm.ifscCode.trim()) {
      toast.error('Beneficiary name, account number and IFSC code are required');
      return;
    }
    setAddingBankAccount(true);
    try {
      await axios.post(`/api/admin/institutions/${institution._id}/linked-account`, {
        bankAccount: {
          beneficiaryName: bankForm.beneficiaryName.trim(),
          bankName: bankForm.bankName.trim(),
          accountNumber: bankForm.accountNumber.trim(),
          ifscCode: bankForm.ifscCode.trim().toUpperCase(),
        },
      });
      toast.success('Bank account linked and split payments enabled');
      setBankForm({ beneficiaryName: '', bankName: '', accountNumber: '', ifscCode: '' });
      await fetchCollegeDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add bank account');
    } finally {
      setAddingBankAccount(false);
    }
  };

  const handleNewCourseProgrammeChange = (programmeId) => {
    setNewCourse(prev => ({
      ...prev,
	      programmeId,
	      category: institutionCategory,
	      position: '',
    }));
  };

  const handleEditingCourseProgrammeChange = (programmeId) => {
    setEditingCourse(prev => ({
      ...prev,
	      programmeId,
	      category: institutionCategory,
	      position: '',
    }));
  };

  const fetchErpPlatforms = async () => {
    if (!canConnectErp || !institution?._id) return;
    setErpLoading(true);
    try {
      const res = await axios.get('/api/erp/platforms', {
        params: user?.role === 'cluster_admin' ? { institutionId: institution._id } : {},
      });
      setErpPlatforms(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ERP platforms');
    } finally {
      setErpLoading(false);
    }
  };

  const openBankDetails = () => {
    setShowBankDetails(true);
    fetchErpPlatforms();
  };

  const handleConnectErp = async (platform) => {
    if (!platform?.slug || !institution?._id) return;
    setConnectingErp(platform.slug);
    try {
	      const res = await axios.post(`/api/erp/platforms/${platform.slug}/connect`, {
	        ...(user?.role === 'cluster_admin' ? { institutionId: institution._id } : {}),
	        returnPath: `${window.location.pathname}${window.location.search}`,
	      });
      if (res.data?.authorizationUrl) {
        window.location.href = res.data.authorizationUrl;
      } else {
        toast.error('Authorization URL was not returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start ERP connection');
      setConnectingErp('');
    }
	  };

  const handleSelectOrganization = async (platform, selectedOrganizationId = '') => {
    const connectionId = platform.connection?.id;
    const organizationId = selectedOrganizationId || selectedOrganizations[connectionId] || platform.connection?.organizationId || '';
    if (!connectionId || !organizationId) {
      toast.error('Select a Zoho organization');
      return;
    }
    setSavingOrganization(connectionId);
    try {
      await axios.post(`/api/erp/connections/${connectionId}/organization`, {
        organizationId,
        ...(user?.role === 'cluster_admin' ? { institutionId: institution._id } : {}),
      });
      toast.success('Zoho organization connected');
      fetchErpPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save Zoho organization');
    } finally {
      setSavingOrganization('');
    }
  };

  const fieldValue = (field) => field?.field_id || field?.api_name || field?.label || '';

  const currentMappingValue = (connection, entity, seatifyKey) => {
    const local = fieldMappings[connection?.id]?.[entity]?.[seatifyKey];
    if (local !== undefined) return local;
    const saved = connection?.fieldMappings?.[entity]?.[seatifyKey];
    return fieldValue(saved);
  };

  const handleFieldMappingChange = (connectionId, entity, seatifyKey, value) => {
    setFieldMappings(prev => ({
      ...prev,
      [connectionId]: {
        ...(prev[connectionId] || {}),
        [entity]: {
          ...((prev[connectionId] || {})[entity] || {}),
          [seatifyKey]: value,
        },
      },
    }));
  };

  const handleSaveFieldMappings = async (platform) => {
    const connection = platform.connection;
    if (!connection?.id) return;
    const mappings = { contact: {}, invoice: {} };
    ['contact', 'invoice'].forEach((entity) => {
      Object.keys(connection.seatifyFields || {}).forEach((seatifyKey) => {
        mappings[entity][seatifyKey] = currentMappingValue(connection, entity, seatifyKey);
      });
    });

    setSavingFieldMappings(connection.id);
    try {
      await axios.post(`/api/erp/connections/${connection.id}/field-mappings`, {
        mappings,
        ...(user?.role === 'cluster_admin' ? { institutionId: institution._id } : {}),
      });
      toast.success('Zoho field mappings saved');
      fetchErpPlatforms();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save Zoho field mappings');
    } finally {
      setSavingFieldMappings('');
    }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;
  const logoUrl = getAssetUrl(institution?.logoPath);
  const bankAccount = institution?.bankAccount;
  const bankStatus = String(bankAccount?.verificationStatus || institution?.razorpayProductStatus || 'not submitted').replace(/_/g, ' ');
  const statusValue = String(bankAccount?.verificationStatus || institution?.razorpayProductStatus || '').toLowerCase();
  const bankStatusClass = ['active', 'activated'].includes(statusValue)
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : statusValue.includes('clarification')
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';
  const detailRows = [
    ['Beneficiary Name', bankAccount?.beneficiaryName],
    ['Bank Name', bankAccount?.bankName],
    ['Account Number', bankAccount?.accountNumber],
    ['IFSC Code', bankAccount?.ifscCode],
    ['Split Payments', institution?.splitPaymentsEnabled === false ? 'Disabled' : 'Enabled'],
  ];
  const erpPagePath = isInstitutionScope
    ? '/institution/erp'
    : `${user?.role === 'cluster_admin' ? '/cluster-admin' : '/admin'}/colleges/${encodeURIComponent(institution?.displayName || institution?.collegeName || collegeName)}/erp`;
  const formatMoney = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const formatDate = (value) => value
    ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'Not available';

  return (
    <div>
      <div className="mb-8">
        <Link to={isInstitutionScope ? '/institution' : '/admin/colleges'} className="inline-flex items-center gap-2 text-indigo-600 font-bold mb-4 hover:text-indigo-700 transition-colors">
          <ArrowLeft size={16} /> Back to Institutions
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center overflow-hidden border border-slate-100">
              {logoUrl ? <img src={logoUrl} alt={decodeURIComponent(collegeName)} className="w-full h-full object-contain p-1 bg-white" /> : <Building size={32} />}
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>
                {institution?.displayName || decodeURIComponent(collegeName)}
              </h1>
              {!isSuperAdmin && <p className="text-slate-500 mt-1">{programmes.length} Programmes · {courses.length} Courses Available</p>}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={openBankDetails}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:bg-slate-50 transition-colors"
            >
              <Landmark size={17} />
              View Bank Details
            </button>
            {canCreateCourses && <button
              onClick={() => setShowAddProgramme(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-xl hover:bg-slate-50 transition-colors"
            >
              <Plus size={17} />
              Create Programme
            </button>}
            {canCreateCourses && <button
              onClick={() => setShowAddCourse(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors"
            >
              <Plus size={17} />
              Create Course
            </button>}
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {[
              { label: 'Onboarded On', value: formatDate(institution?.createdAt), icon: CalendarDays, color: 'text-blue-600 bg-blue-50' },
              { label: 'Total Collected', value: formatMoney(incomeSummary.totalCollected), icon: WalletCards, color: 'text-slate-700 bg-slate-100' },
              { label: 'Platform Revenue', value: formatMoney(incomeSummary.totalPlatformFee), icon: IndianRupee, color: 'text-indigo-600 bg-indigo-50' },
              { label: 'Transactions', value: Number(incomeSummary.settledCount || 0) + Number(incomeSummary.pendingCount || 0), icon: FileText, color: 'text-emerald-600 bg-emerald-50' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className={`mb-3 inline-flex rounded-xl p-2.5 ${color}`}><Icon size={20} /></div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
                <p className="mt-1 text-2xl font-extrabold text-slate-800">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-extrabold text-slate-800">Platform Revenue Trend</h2>
              <p className="mb-5 text-sm text-slate-500">Monthly platform income generated by this institution.</p>
              {monthlyRevenue.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyRevenue}>
                      <defs>
                        <linearGradient id="institutionRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `₹${Number(value).toLocaleString('en-IN')}`} />
                      <Tooltip formatter={(value) => formatMoney(value)} />
                      <Area type="monotone" dataKey="platformRevenue" name="Platform Revenue" stroke="#4f46e5" strokeWidth={3} fill="url(#institutionRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="flex h-72 items-center justify-center text-sm font-semibold text-slate-400">No payment revenue recorded yet.</div>}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-extrabold text-slate-800">Institution Details</h2>
              <div className="mt-5 space-y-4 text-sm">
                {[
                  ['Status', institution?.active === false ? 'Inactive' : 'Active'],
                  ['Onboarding', institution?.onboardingStatus || 'Pending'],
                  ['Cluster', institution?.cluster?.name || 'Not assigned'],
                  ['Category', institution?.category || 'Not provided'],
                  ['Contact', institution?.contactName || 'Not provided'],
                  ['Email', institution?.contactEmail || 'Not provided'],
                  ['Phone', institution?.contactPhone || 'Not provided'],
                  ['Split Payments', institution?.splitPaymentsEnabled === false ? 'Disabled' : 'Enabled'],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-3">
                    <span className="font-semibold text-slate-400">{label}</span>
                    <span className="text-right font-bold capitalize text-slate-700">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

	      {!isSuperAdmin && (
	        <div className="space-y-6">
	          <div className="w-max grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
	            {[
	              { id: 'courses', label: `Courses (${courses.length})` },
	              { id: 'programmes', label: `Programmes (${programmes.length})` },
	            ].map(item => (
	              <button
	                key={item.id}
	                type="button"
	                onClick={() => setListMode(item.id)}
	                className={`rounded-xl px-4 py-3 text-sm font-extrabold transition-all ${
	                  listMode === item.id
	                    ? 'bg-indigo-600 text-white shadow-md'
	                    : 'text-slate-600 hover:bg-slate-50'
	                }`}
	              >
	                {item.label}
	              </button>
	            ))}
	          </div>

	          {listMode === 'courses' && (
	            courses.length === 0 ? (
	              <div className="bg-white p-8 rounded-2xl text-center text-slate-500 shadow-sm border border-slate-200">No courses found for this institution.</div>
	            ) : (
	              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
	                {courses.map(course => (
	                  <div key={course._id || course.id} className="border border-slate-200 rounded-2xl p-6 bg-white hover:border-indigo-300 hover:shadow-lg transition-all flex flex-col">
	                    <div className="mb-4 flex items-start justify-between gap-3">
	                      <div>
	                        <h3 className="font-bold text-lg text-slate-800 leading-tight">{course.name}</h3>
	                        <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider mt-1">{course.category}</p>
	                      </div>
	                      {canEditCourses && <button
	                        type="button"
	                        onClick={() => openEditCourse(course)}
	                        className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
	                        aria-label={`Edit ${course.name}`}
	                      >
	                        <Pencil size={16} />
	                      </button>}
	                    </div>
	                    <div className="mt-auto space-y-2 border-t border-slate-100 pt-4">
	                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Programme</h4>
	                      <div className="flex items-center justify-between text-sm p-3 bg-slate-50 rounded-xl border border-slate-100">
	                        <span className="font-medium text-slate-700 flex items-center gap-2"><BookOpen size={14} className="text-slate-400"/> {course.programmeName || course.programme?.name}</span>
	                        <span className="font-bold text-emerald-600">₹{course.fee}</span>
	                      </div>
	                      <p className="text-xs text-slate-500">{course.minimumQualification || 'Minimum qualification not set'}</p>
	                    </div>
	                  </div>
	                ))}
	              </div>
	            )
	          )}

	          {listMode === 'programmes' && (
	            programmes.length === 0 ? (
	              <div className="bg-white p-8 rounded-2xl text-center text-slate-500 shadow-sm border border-slate-200">No programmes found for this institution.</div>
	            ) : (
	              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
	                {programmes.map(programme => (
	                  <div key={programme._id || programme.id} className="border border-slate-200 rounded-2xl p-6 bg-white hover:border-indigo-300 hover:shadow-lg transition-all flex flex-col">
	                    <div className="flex items-start justify-between gap-3">
	                      <div className="min-w-0">
	                        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
	                          <BookOpen size={20} />
	                        </div>
	                        <h3 className="font-bold text-lg text-slate-800 leading-tight">{getProgrammeLabel(programme)}</h3>
	                        <p className="mt-1 text-xs font-bold uppercase tracking-wider text-indigo-500">{programme.category}</p>
	                      </div>
	                      {canEditCourses && (
	                        <button
	                          type="button"
	                          onClick={() => openEditProgramme(programme)}
	                          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
	                          aria-label={`Edit ${getProgrammeLabel(programme)}`}
	                        >
	                          <Pencil size={16} />
	                        </button>
	                      )}
	                    </div>
	                    <p className="mt-4 flex-1 text-sm leading-6 text-slate-500">{programme.description || 'No description added.'}</p>
	                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-bold">
	                      <span className={programme.active === false ? 'text-rose-600' : 'text-emerald-600'}>
	                        {programme.active === false ? 'Inactive' : 'Active'}
	                      </span>
	                      <span className="text-slate-400">{programme.position ? `Position ${programme.position}` : 'Alphabetical'}</span>
	                    </div>
	                  </div>
	                ))}
	              </div>
	            )
	          )}
	        </div>
	      )}

      {showAddProgramme && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[calc(100svh-2rem)] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="shrink-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Create Programme</h2>
                <p className="text-sm text-slate-500">{institution?.displayName || institution?.collegeName}</p>
              </div>
              <button onClick={() => setShowAddProgramme(false)} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
                <X size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setShowBulkUploadProgrammes(true)}
                className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50 text-sm font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                <Upload size={16} />
                Bulk Upload Programmes
              </button>
		              <DialogField label="Programme Name *">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter programme name" value={newProgramme.name} onChange={(e) => setNewProgramme(prev => ({ ...prev, name: e.target.value }))} />
		              </DialogField>
		              <DialogField label="Slug">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Optional URL slug" value={newProgramme.slug} onChange={(e) => setNewProgramme(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} />
		              </DialogField>
		              <DialogField label="Position" help="Position controls programme display order. Position 1 appears first; leave it empty for alphabetical order. The slug is only the URL identifier and does not control ordering.">
		                <select
		                className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
		                value={newProgramme.position}
		                onChange={(e) => setNewProgramme(prev => ({ ...prev, position: e.target.value }))}
		              >
	                <option value="">Position (optional — alphabetical)</option>
	                {Array.from({ length: programmes.length + 1 }, (_, index) => index + 1).map(position => (
		                  <option key={position} value={position}>{position}</option>
		                ))}
		              </select>
		              </DialogField>
		              <DialogField label="Category">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-slate-100 text-slate-600 cursor-not-allowed" value={institutionCategoryLabel} disabled />
		              </DialogField>
	              <DialogField label="Description" className="md:col-span-2">
	                <textarea className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 min-h-24" placeholder="Enter programme description" value={newProgramme.description} onChange={(e) => setNewProgramme(prev => ({ ...prev, description: e.target.value }))} />
	              </DialogField>
            </div>
            <div className="shrink-0 z-10 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowAddProgramme(false)} className="px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreateProgramme} className="px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700">Create Programme</button>
            </div>
          </div>
        </div>
      )}

      {showAddCourse && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[calc(100svh-2rem)] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
            <div className="shrink-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Create Course</h2>
                <p className="text-sm text-slate-500">Select one of this institution's programmes.</p>
              </div>
              <button onClick={() => setShowAddCourse(false)} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
                <X size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setShowBulkUploadCourses(true)}
                className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50 text-sm font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                <Upload size={16} />
                Bulk Upload Courses
              </button>
		              <DialogField label="Programme *" className="md:col-span-2">
		                <select className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={newCourse.programmeId} onChange={(e) => handleNewCourseProgrammeChange(e.target.value)}>
		                <option value="">Select Programme *</option>
	                {visibleProgrammes.map(programme => (
	                  <option key={programme._id || programme.id} value={programme._id || programme.id}>{getProgrammeLabel(programme)}</option>
		                ))}
		              </select>
		              </DialogField>
		              <DialogField label="Course Name *" className="md:col-span-2">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter course name" value={newCourse.name} onChange={(e) => setNewCourse(prev => ({ ...prev, name: e.target.value }))} />
		              </DialogField>
		              <DialogField label="Category">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-slate-100 text-slate-600 cursor-not-allowed" value={institutionCategoryLabel} disabled />
		              </DialogField>
	              <DialogField label="Pre-registration Fee *">
	                <input type="number" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter fee" value={newCourse.fee} onChange={(e) => setNewCourse(prev => ({ ...prev, fee: e.target.value }))} />
	              </DialogField>
	              <DialogField label="Seats">
	                <input type="number" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter total seats" value={newCourse.seats} onChange={(e) => setNewCourse(prev => ({ ...prev, seats: e.target.value }))} />
	              </DialogField>
	              <DialogField
	                label={newCourse.programmeId ? `Position under ${programmeLabelById(newCourse.programmeId)}` : 'Course Position'}
	                help={`This list is based only on courses under ${programmeLabelById(newCourse.programmeId) || 'the selected programme'}. Position 1 appears first. A new course can be placed from 1 to the programme's new total. Leave it empty for alphabetical order.`}
	              >
	                <select disabled={!newCourse.programmeId} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-slate-100 disabled:text-slate-400" value={newCourse.position} onChange={(e) => setNewCourse(prev => ({ ...prev, position: e.target.value }))}>
	                  <option value="">{newCourse.programmeId ? 'No position — alphabetical' : 'Select a programme first'}</option>
	                  {coursePositionOptions(newCourse.programmeId).map(position => (
	                    <option key={position} value={position}>{position}</option>
	                  ))}
	                </select>
	              </DialogField>
	              <DialogField label="Minimum Qualification">
	                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter minimum qualification" value={newCourse.minimumQualification} onChange={(e) => setNewCourse(prev => ({ ...prev, minimumQualification: e.target.value }))} />
	              </DialogField>
              <label className="md:col-span-2 flex items-center justify-between gap-4 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50">
                <span className="text-sm font-bold text-slate-700">Lateral entry accepted</span>
                <input type="checkbox" checked={Boolean(newCourse.lateralEntryAccepted)} onChange={(e) => setNewCourse(prev => ({ ...prev, lateralEntryAccepted: e.target.checked }))} className="h-5 w-5 accent-indigo-600" />
              </label>
            </div>
            <div className="shrink-0 z-10 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowAddCourse(false)} className="px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreateCourse} className="px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700">Create Course</button>
            </div>
          </div>
        </div>
	      )}

	      {editingCourse && (
	        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
	          <div className="bg-white w-full max-w-2xl max-h-[calc(100svh-2rem)] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
	            <div className="shrink-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
	              <div>
	                <h2 className="text-xl font-extrabold text-slate-900">Edit Course</h2>
	                <p className="text-sm text-slate-500">{institution?.displayName || institution?.collegeName}</p>
	              </div>
	              <button onClick={() => setEditingCourse(null)} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
	                <X size={20} />
	              </button>
	            </div>
	            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
			              <DialogField label="Programme *" className="md:col-span-2">
			                <select className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={editingCourse.programmeId} onChange={(e) => handleEditingCourseProgrammeChange(e.target.value)}>
			                <option value="">Select Programme *</option>
		                {visibleProgrammes.map(programme => (
		                  <option key={programme._id || programme.id} value={programme._id || programme.id}>{getProgrammeLabel(programme)}</option>
			                ))}
			              </select>
			              </DialogField>
			              <DialogField label="Course Name *" className="md:col-span-2">
			                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter course name" value={editingCourse.name} onChange={(e) => setEditingCourse(prev => ({ ...prev, name: e.target.value }))} />
			              </DialogField>
			              <DialogField label="Category">
			                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none bg-slate-100 text-slate-600 cursor-not-allowed" value={institutionCategoryLabel} disabled />
			              </DialogField>
		              <DialogField label="Pre-registration Fee *">
		                <input type="number" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter fee" value={editingCourse.fee} onChange={(e) => setEditingCourse(prev => ({ ...prev, fee: e.target.value }))} />
		              </DialogField>
		              <DialogField label="Total Seats">
		                <input type="number" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter total seats" value={editingCourse.seats} onChange={(e) => setEditingCourse(prev => ({ ...prev, seats: e.target.value }))} />
		              </DialogField>
		              <DialogField label="Seats Available">
		                <input type="number" className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter available seats" value={editingCourse.seatsAvailable} onChange={(e) => setEditingCourse(prev => ({ ...prev, seatsAvailable: e.target.value }))} />
		              </DialogField>
		              <DialogField
		                label={editingCourse.programmeId ? `Position under ${programmeLabelById(editingCourse.programmeId)}` : 'Course Position'}
		                help={`This list is based only on courses under ${programmeLabelById(editingCourse.programmeId) || 'the selected programme'}. Position 1 appears first. Leave it empty for alphabetical order.`}
		              >
		                <select disabled={!editingCourse.programmeId} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-slate-100 disabled:text-slate-400" value={editingCourse.position} onChange={(e) => setEditingCourse(prev => ({ ...prev, position: e.target.value }))}>
		                  <option value="">{editingCourse.programmeId ? 'No position — alphabetical' : 'Select a programme first'}</option>
		                  {coursePositionOptions(editingCourse.programmeId, editingCourse.id).map(position => (
		                    <option key={position} value={position}>{position}</option>
		                  ))}
		                </select>
		              </DialogField>
		              <DialogField label="Minimum Qualification" className="md:col-span-2">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter minimum qualification" value={editingCourse.minimumQualification} onChange={(e) => setEditingCourse(prev => ({ ...prev, minimumQualification: e.target.value }))} />
		              </DialogField>
                <label className="md:col-span-2 flex items-center justify-between gap-4 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50">
                  <span className="text-sm font-bold text-slate-700">Lateral entry accepted</span>
                  <input type="checkbox" checked={Boolean(editingCourse.lateralEntryAccepted)} onChange={(e) => setEditingCourse(prev => ({ ...prev, lateralEntryAccepted: e.target.checked }))} className="h-5 w-5 accent-indigo-600" />
                </label>
	              <label className="md:col-span-2 flex items-center justify-between gap-4 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50">
	                <span className="text-sm font-bold text-slate-700">Course Active</span>
	                <input type="checkbox" checked={editingCourse.active} onChange={(e) => setEditingCourse(prev => ({ ...prev, active: e.target.checked }))} className="h-5 w-5 accent-indigo-600" />
	              </label>
	            </div>
	            <div className="shrink-0 z-10 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
	              <button onClick={() => setEditingCourse(null)} className="px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
	              <button onClick={handleUpdateCourse} className="px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700">Save Changes</button>
	            </div>
	          </div>
	        </div>
	      )}

	      {editingProgramme && (
	        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
	          <div className="bg-white w-full max-w-2xl max-h-[calc(100svh-2rem)] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
	            <div className="shrink-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
	              <div>
	                <h2 className="text-xl font-extrabold text-slate-900">Edit Programme</h2>
	                <p className="text-sm text-slate-500">{institution?.displayName || institution?.collegeName}</p>
	              </div>
	              <button onClick={() => setEditingProgramme(null)} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
	                <X size={20} />
	              </button>
	            </div>
	            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
		              <DialogField label="Programme Name *">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter programme name" value={editingProgramme.name} onChange={(e) => setEditingProgramme(prev => ({ ...prev, name: e.target.value }))} />
		              </DialogField>
		              <DialogField label="Slug">
		                <input className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Enter URL slug" value={editingProgramme.slug} onChange={(e) => setEditingProgramme(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} />
		              </DialogField>
		              <DialogField label="Position" help="Position controls programme display order. Position 1 appears first; leave it empty for alphabetical order. The slug is only the URL identifier and does not control ordering.">
		                <select className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" value={editingProgramme.position} onChange={(e) => setEditingProgramme(prev => ({ ...prev, position: e.target.value }))}>
		                <option value="">No position — alphabetical</option>
	                {Array.from({ length: programmes.length }, (_, index) => index + 1).map(position => (
		                  <option key={position} value={position}>{position}</option>
		                ))}
		              </select>
		              </DialogField>
		              <label className="flex items-center gap-3 px-4 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-700">
	                <input type="checkbox" checked={editingProgramme.active} onChange={(e) => setEditingProgramme(prev => ({ ...prev, active: e.target.checked }))} className="h-5 w-5 accent-indigo-600" />
	                Programme Active
	              </label>
		              <DialogField label="Description" className="md:col-span-2">
		                <textarea className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 min-h-28" placeholder="Enter programme description" value={editingProgramme.description} onChange={(e) => setEditingProgramme(prev => ({ ...prev, description: e.target.value }))} />
		              </DialogField>
	            </div>
	            <div className="shrink-0 z-10 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
	              <button onClick={() => setEditingProgramme(null)} className="px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
	              <button onClick={handleUpdateProgramme} className="px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700">Save Changes</button>
	            </div>
	          </div>
	        </div>
	      )}

	      {showBankDetails && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Bank Details</h2>
                <p className="text-sm text-slate-500">{institution?.displayName || institution?.collegeName}</p>
              </div>
              <button onClick={() => setShowBankDetails(false)} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
                <X size={20} />
              </button>
            </div>
	            <div className="p-6 space-y-5">
		              {!bankAccount ? (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-500">
                        No active bank account details found. Split payments are disabled until an account is added.
                      </div>
                      {!isInstitutionScope && canEditLinkedAccounts && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input className="px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Beneficiary Name *" value={bankForm.beneficiaryName} onChange={(e) => setBankForm(prev => ({ ...prev, beneficiaryName: e.target.value }))} />
                            <input className="px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Bank Name" value={bankForm.bankName} onChange={(e) => setBankForm(prev => ({ ...prev, bankName: e.target.value }))} />
                            <input className="px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Account Number *" value={bankForm.accountNumber} onChange={(e) => setBankForm(prev => ({ ...prev, accountNumber: e.target.value }))} />
                            <input className="px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 uppercase" placeholder="IFSC Code *" value={bankForm.ifscCode} onChange={(e) => setBankForm(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))} />
                          </div>
                          <button
                            onClick={handleAddBankAccount}
                            disabled={addingBankAccount}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:bg-indigo-400"
                          >
                            <Landmark size={17} />
                            {addingBankAccount ? 'Creating Razorpay Account...' : 'Add Bank Account'}
                          </button>
                        </>
                      )}
                    </div>
	              ) : (
                <div className="space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Route Status</p>
                      <p className="text-sm font-bold text-slate-800 capitalize">{bankStatus}</p>
                    </div>
                    <span className={`w-max text-xs font-bold px-2.5 py-1 rounded-full border capitalize ${bankStatusClass}`}>
                      {bankStatus}
                    </span>
	                  </div>
		                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {detailRows.map(([label, value]) => (
                      <div key={label} className="rounded-xl border border-slate-100 p-4">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                        <p className="mt-1 text-sm font-semibold text-slate-800 break-all">{value || '—'}</p>
                      </div>
                    ))}
                  </div>
	                </div>
		              )}
		              {canConnectErp && (
		                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
		                  <div>
		                    <h3 className="text-base font-extrabold text-slate-900">Connect your ERP Platform</h3>
		                    <p className="text-sm text-slate-500">Open the ERP workspace to connect platforms and map their columns to Seatify fields.</p>
		                  </div>
		                  <Link
		                    to={erpPagePath}
		                    onClick={() => setShowBankDetails(false)}
		                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
		                  >
		                    Open ERP Platform
		                  </Link>
		                </div>
		              )}
	            </div>
            <div className="border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {bankAccount && !isInstitutionScope && canEditLinkedAccounts ? (
                <button
                  onClick={handleDeleteLinkedAccount}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-50"
                >
                  <Trash2 size={16} />
                  Remove Linked Account
                </button>
              ) : <span />}
              <button onClick={() => setShowBankDetails(false)} className="px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modals */}
      {!isSuperAdmin && <BulkUploadModal
        isOpen={showBulkUploadCourses}
        onClose={() => {
          setShowBulkUploadCourses(false);
          fetchCollegeDetails();
        }}
        institutionId={institution?._id}
        programmes={programmes}
        type="courses"
        apiBase="/api"
      />}
      {!isSuperAdmin && <BulkUploadModal
        isOpen={showBulkUploadProgrammes}
        onClose={() => {
          setShowBulkUploadProgrammes(false);
          fetchCollegeDetails();
        }}
        institutionId={institution?._id}
        programmes={programmes}
        type="programmes"
        apiBase="/api"
      />}
    </div>
  );
}
